/**
 * Runs gruppiert statt als flache Liste (/runs, seit 29.09.2026).
 *
 * ANLASS (Robert, 29.09.2026): „der screen ist nicht so cool". Vier Fassungen
 * derselben Headline standen als vier unverbundene Zeilen da. Die eigentliche
 * Geschichte, dass die Vergleichsfassung von 2,4 auf 5,7 aufholte, während die
 * eigene bei 6,3 bis 6,7 blieb, war nur mit Kopfrechnen zu sehen.
 *
 * WAS ZUSAMMENGEHÖRT: gleicher Tag, gleiche Studie, gleiche Zielgruppe,
 * gleiche Textart. Das ist eine Annäherung an „Fassungen desselben Textes",
 * kein Beweis: die Liste kennt nur die erste Zeile des Texts (`auszug`), und
 * eine Studie oder Zielgruppe kann an einem Tag zwei verschiedene Texte
 * tragen. Dann stehen sie in einer Gruppe, was harmlos ist; die Zeilen zeigen
 * weiter jeden Text im Wortlaut. Kein neues Feld am Server.
 */

import type { Lauf } from './api';
import { fassungsname } from './fassungsname';
import { locale, t } from './sprache';

export interface RunGruppe {
  schluessel: string;
  /** Studie, gemeinsamer Anfang der Texte oder der eine Text. */
  titel: string;
  /** true: `titel` ist geprüfter Text und steht in Anführungszeichen. */
  zitat: boolean;
  /** Textart, Zielgruppe, Tag; leere Teile fehlen. */
  etikett: string[];
  /** Älteste zuerst, damit F1, F2, … der Reihenfolge des Arbeitens folgen. */
  laeufe: Lauf[];
  /** Vergleiche mit Score, älteste zuerst. Leer, wenn es weniger als zwei gibt. */
  verlauf: Lauf[];
  credits: number;
  /** Jüngster Zeitpunkt der Gruppe, für die Reihenfolge der Gruppen. */
  zuletzt: string;
}

/** Ab so vielen gemeinsamen Wörtern trägt der Anfang als Titel. */
const MIN_WOERTER = 3;

function tagSchluessel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'unbekannt';
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

/** „heute", „gestern" oder das Datum, in lokaler Zeit wie die Uhrzeiten der Zeilen. */
export function tagText(iso: string, jetzt: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const tag = tagSchluessel(iso);
  if (tag === tagSchluessel(jetzt.toISOString())) return t('heute', 'today');
  const gestern = new Date(jetzt);
  gestern.setDate(gestern.getDate() - 1);
  if (tag === tagSchluessel(gestern.toISOString())) return t('gestern', 'yesterday');
  return d.toLocaleDateString(locale(), { day: '2-digit', month: '2-digit', year: '2-digit' });
}

/**
 * Der gemeinsame Anfang mehrerer Texte, wortweise. Nie mitten im Wort: „Wir
 * messen Erfolg an Anfragen" und „… an qualifizierten" ergeben „… an".
 */
export function gemeinsamerAnfang(texte: string[]): string | null {
  if (texte.length < 2) return null;
  const woerter = texte.map(t => t.trim().split(/\s+/));
  const [erste] = woerter;
  if (!erste) return null;
  let n = 0;
  while (n < erste.length && woerter.every(w => w[n] === erste[n])) n++;
  if (n < MIN_WOERTER) return null;
  // Der ganze Text ist gemeinsam: dann gibt es nichts abzukürzen.
  if (woerter.every(w => w.length === n)) return erste.join(' ');
  return erste.slice(0, n).join(' ').replace(/[,;:–—-]+$/, '') + ' …';
}

export function gruppiere(
  laeufe: Lauf[],
  textart: Record<string, string>,
  jetzt: Date = new Date(),
): RunGruppe[] {
  const nachSchluessel = new Map<string, Lauf[]>();
  for (const l of laeufe) {
    const s = [tagSchluessel(l.created_at), l.study ?? '', l.set, l.asset].join('|');
    const liste = nachSchluessel.get(s);
    if (liste) liste.push(l);
    else nachSchluessel.set(s, [l]);
  }

  const gruppen: RunGruppe[] = [];
  for (const [schluessel, liste] of nachSchluessel) {
    const alt = [...liste].sort((a, b) => a.created_at.localeCompare(b.created_at));
    const erster = alt[0];
    if (!erster) continue;
    const texte = alt.map(l => l.text).filter((t): t is string => !!t);
    const [einziger] = texte;
    const anfang = gemeinsamerAnfang(texte);

    let titel: string;
    let zitat = false;
    if (erster.study) titel = erster.study;
    else if (anfang) { titel = anfang; zitat = true; }
    else if (einziger && texte.length === 1 && alt.length === 1) { titel = einziger; zitat = true; }
    // Ein einzelner Run ohne Text hiess hier „1 Runs“ (gesehen an einer
    // Vorauswahl, 03.10.2026). Dann sagt der Titel, was lief.
    else if (alt.length === 1) titel = erster.mode === 'vorauswahl' ? t('Vorauswahl mehrerer Fassungen', 'Preselection of several versions') : fassungsname(erster.variant) || t('Run ohne Namen', 'Unnamed run');
    else titel = t(`${alt.length} Runs`, `${alt.length} runs`);

    const vergleiche = alt.filter(l => l.mode === 'compare' && l.scores);
    gruppen.push({
      schluessel,
      titel,
      zitat,
      etikett: [
        textart[erster.asset] ?? erster.asset,
        erster.set_name || erster.set,
        tagText(erster.created_at, jetzt),
      ].filter(Boolean),
      laeufe: alt,
      verlauf: vergleiche.length >= 2 ? vergleiche : [],
      credits: alt.reduce((s, l) => s + (l.credits ?? 0), 0),
      zuletzt: (alt.at(-1) ?? erster).created_at,
    });
  }
  return gruppen.sort((a, b) => b.zuletzt.localeCompare(a.zuletzt));
}

/**
 * Position auf der Skala 0 bis 10 in Prozent, begrenzt. Scores sind gewichtete
 * Noten von 1 bis 10; ein Ausreißer darf den Punkt nicht aus der Bahn schieben.
 */
export function aufSkala(wert: number): number {
  if (!Number.isFinite(wert)) return 0;
  return Math.min(100, Math.max(0, wert * 10));
}
